import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Layout from '../components/Layout';
import Container from '../components/Container';
import Button from '../components/Button';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  padding: 7rem 0 4rem;
  background: linear-gradient(135deg, #0e1633 0%, #1e2a4a 100%);
  min-height: 85vh;
  animation: ${fadeIn} 0.8s ease-out;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  max-width: 800px;
  margin: 0 auto;
  background: rgba(20, 30, 60, 0.85);
  border-radius: 24px;
  box-shadow: 0 8px 32px 0 #2d6cff33;
  border: 1.5px solid #2d6cff44;
  animation: ${fadeIn} 1s ease-out 0.2s both;
  color: #fff;
`;

const SuccessIcon = styled.div`
  font-size: 5rem;
  color: #2ecc71;
  margin-bottom: 2rem;
`;

const SuccessHeader = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: #6ea8ff;
  text-shadow: 0 0 16px #2d6cff, 0 2px 10px #0008;
`;

const SuccessMessage = styled.div`
  color: #2ecc71;
  background: linear-gradient(90deg, #1e2a4a 60%, #2ecc7122 100%);
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const DownloadSection = styled.div`
  background: rgba(30, 42, 74, 0.85);
  border-radius: 16px;
  padding: 2rem;
  margin: 2rem 0;
  text-align: left;
  border: 1.5px solid #2d6cff22;
  color: #b6d0ff;
`;

const DownloadTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #6ea8ff;
`;

const DownloadInstructions = styled.div`
  margin-bottom: 2rem;
  color: #b6d0ff;
  ol {
    padding-left: 1.5rem;
    margin-bottom: 1.5rem;
    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const LicenseKey = styled.div`
  background: rgba(30, 42, 74, 0.85);
  border: 1.5px dashed #2d6cff88;
  padding: 1rem;
  font-family: monospace;
  font-size: 1.1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  color: #fff;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
`;

// New styled components for registration form
const FormSection = styled.div`
  background: rgba(30, 42, 74, 0.85);
  border-radius: 16px;
  padding: 2rem;
  margin: 2rem 0;
  border: 1.5px solid #2d6cff22;
  color: #b6d0ff;
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #0070f3;
    box-shadow: 0 0 0 3px rgba(0, 112, 243, 0.1);
  }
`;

const UserCredentials = styled.div`
  background: rgba(30, 42, 74, 0.85);
  border: 1.5px dashed #2d6cff88;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  font-family: monospace;
  font-size: 1.1rem;
  color: #fff;
`;

const CredentialItem = styled.div`
  margin-bottom: 1rem;
  &:last-child { margin-bottom: 0; }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  background: linear-gradient(90deg, #1e2a4a 60%, #ff525222 100%);
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const CountdownWrapper = styled.div`
  margin: 1rem 0;
  font-size: 1.1rem;
  text-align: center;
  color: #ef4444;
  font-weight: bold;
`;

const CountdownTime = styled.span`
  background: #fee2e2;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  margin: 0 0.25rem;
  font-family: monospace;
`;

const HiddenCredentialsWrapper = styled.div`
  text-align: center;
  margin: 2rem 0;
  color: #b6d0ff;
`;

const RevealButton = styled(Button)`
  margin: 0 auto;
  max-width: 250px;
`;

function Countdown({ seconds }) {
  const [timeLeft, setTimeLeft] = useState(seconds);
  
  useEffect(() => {
    if (timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prevTime => prevTime - 1);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeLeft]);
  
  // Format seconds into MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  return (
    <CountdownWrapper>
      Credentials will be hidden in <CountdownTime>{formatTime(timeLeft)}</CountdownTime>
    </CountdownWrapper>
  );
}

function SuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const licenseKey = location.state?.licenseKey || "License key not found";
  const paymentId = location.state?.paymentId;
  const planId = location.state?.planId || "monthly"; // Default to monthly if not provided
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userCredentials, setUserCredentials] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [credentialsViewed, setCredentialsViewed] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [credentialsRevealed, setCredentialsRevealed] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);
  
  // Fetch latest version from GitHub releases
  useEffect(() => {
    const fetchLatestVersion = async () => {
      try {
        const response = await fetch('https://api.github.com/repos/deservedd/deservedwarmer/releases/latest');
        const data = await response.json();
        if (data.tag_name) {
          // Remove 'v' prefix if present
          setLatestVersion(data.tag_name.replace(/^v/, ''));
        }
      } catch (err) {
        console.error('Error fetching latest version:', err);
        // Fallback to a default version if fetch fails
        setLatestVersion('1.0.2');
      }
    };

    fetchLatestVersion();
  }, []);

  // Function to get download URLs
  const getDownloadUrl = (filename) => {
    if (!latestVersion) return '';
    return `https://github.com/deservedd/deservedwarmer/releases/latest/download/${filename.replace('VERSION', latestVersion)}`;
  };
  
  // Check payment status to see if credentials were already viewed
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (paymentId) {
        try {
          const response = await fetch(`/api/payments/${paymentId}/status`);
          const data = await response.json();
          
          if (data.success && data.payment.credentials_viewed) {
            setCredentialsViewed(true);
          } 
        } catch (err) {
          console.error("Error checking payment status:", err);
        }
      }
    };
    
    checkPaymentStatus();
  }, [paymentId]);
  
  const markCredentialsAsViewed = async () => {
    if (paymentId) {
      try {
        const response = await fetch(`/api/payments/${paymentId}/mark-viewed`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          // Only set as revealed, but don't set as viewed yet
          // This allows the user to see the credentials until they refresh
          setCredentialsRevealed(true);
          setShowCountdown(true);
        }
      } catch (err) {
        console.error("Error marking credentials as viewed:", err);
        setError("Error revealing credentials. Please try again.");
      }
    }
  };
  
  const downloadCredentials = () => {
    if (!userCredentials || !userCredentials.user_id || !userCredentials.access_code) {
      setError("User credentials are not available. Please try again or contact support.");
      return;
    }

    // Format the content for the download file
    const isCamera = paymentInfo?.camera_product || paymentInfo?.plan_id === 'camera_lifetime';
    let content = isCamera ? "CAMERA PRO CREDENTIALS\n\n" : "DESERVED WARMER CREDENTIALS\n\n";
    
    // Add order details
    content += "=== ORDER DETAILS ===\n";
    content += `License Key: ${licenseKey}\n`;
    content += `Product: ${isCamera ? 'Camera Pro - Lifetime' : (planId.charAt(0).toUpperCase() + planId.slice(1))}\n`;
    
    // Add payment details if available
    if (paymentInfo) {
      content += "\n=== PAYMENT INFORMATION ===\n";
      content += `Payment ID: ${paymentInfo.payment_id || paymentId}\n`;
      content += `Date: ${new Date(paymentInfo.created_at || Date.now()).toLocaleDateString()}\n`;
      content += `Amount: $${paymentInfo.price_amount || paymentInfo.amount || "N/A"}\n`;
      
      if (paymentInfo.pay_currency) {
        content += `Payment Currency: ${paymentInfo.pay_currency.toUpperCase()}\n`;
        content += `Payment Amount: ${paymentInfo.pay_amount}\n`;
      }
      
      if (paymentInfo.status) {
        content += `Status: ${paymentInfo.status}\n`;
      }
      
      // Add transaction hash if available
      if (paymentInfo.payin_hash) {
        content += `Transaction Hash: ${paymentInfo.payin_hash}\n`;
      }
    }
    
    // Add user credentials
    content += "\n=== YOUR CREDENTIALS ===\n";
    content += `User ID: ${userCredentials.user_id}\n`;
    content += `Access Code: ${userCredentials.access_code}\n`;
    if (isCamera) {
      content += "\nIMPORTANT: Please save these credentials securely. You will need them to login to Camera Pro.\n";
      content += "\nInstructions:\n";
      content += "1. Download Camera Pro from the provided link\n";
      content += "2. Install and launch the application\n";
      content += "3. Enter your User ID and Access Code when prompted\n";
      content += "4. Upload your ID photo and start using your virtual camera\n";
    } else {
      content += "\nIMPORTANT: Please save these credentials securely. You will need them to access your account.\n";
    }
    content += "\nFor support, contact us at https://deserved.me/docs/contact-support";

    // Create a Blob for the text file
    const blob = new Blob([content], { type: "text/plain" });
    
    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link element
    const link = document.createElement("a");
    link.href = url;
    link.download = "accountwarmer_credentials.txt";
    
    // Append the link to the document, click it, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Revoke the URL to free up memory
    URL.revokeObjectURL(url);
  };
  
  useEffect(() => {
    const generateUserCredentials = async () => {
      try {
        setLoading(true);
        
        // If credentials were already viewed and we're not in the reveal state, don't generate again
        if (credentialsViewed && !credentialsRevealed) {
          setLoading(false);
          return;
        }
        
        // First check if we have license key
        if (!licenseKey || licenseKey === "License key not found") {
          setError("License key is missing. Please try again or contact support.");
          setLoading(false);
          return;
        }

        // Get payment details first to check if it's a camera product
        let isCamera = false;
        if (paymentId) {
          const paymentResponse = await fetch(`/api/payments/${paymentId}/details`);
          const paymentData = await paymentResponse.json();
          
          if (paymentData.success) {
            setPaymentInfo(paymentData.payment);
            isCamera = paymentData.payment.camera_product || paymentData.payment.plan_id === 'camera_lifetime';
          }
        }
        
        if (isCamera) {
          // Generate camera credentials using the same pattern as account warmer
          const credentialsResponse = await fetch('/api/camera/generate-credentials', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              license_key: licenseKey,
              payment_id: paymentId
            }),
          });
          
          const credentialsData = await credentialsResponse.json();
          
          if (credentialsData.success) {
            setUserCredentials({
              user_id: credentialsData.user_id,
              access_code: credentialsData.access_code
            });
            setError(null);
          } else {
            setError(credentialsData.message || "Failed to generate camera credentials. Please try again or contact support.");
          }
        } else {
          // Generate credentials using the correct endpoint and method for account warmer
          const credentialsResponse = await fetch('/api/users/generate-credentials', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              license_key: licenseKey,
              payment_id: paymentId
            }),
          });
          
          const credentialsData = await credentialsResponse.json();
          
          if (credentialsData.success) {
            setUserCredentials({
              user_id: credentialsData.user_id,
              access_code: credentialsData.access_code
            });
            setError(null);
          } else {
            setError(credentialsData.message || "Failed to generate credentials. Please try again or contact support.");
          }
        }
      } catch (err) {
        console.error("Error generating credentials:", err);
        setError("An error occurred. Please try again or contact support.");
      } finally {
        setLoading(false);
      }
    };
    
    if (planId && !userCredentials && (!credentialsViewed || credentialsRevealed)) {
      generateUserCredentials();
    }
  }, [planId, userCredentials, paymentId, licenseKey, credentialsViewed, credentialsRevealed, location.state]);
  
  return (
    <Layout>
      <PageWrapper>
        <Container>
          <SuccessContainer>
            <SuccessHeader>Payment Successful!</SuccessHeader>
            <SuccessMessage>
              Thank you for your purchase. Your payment has been processed successfully.
            </SuccessMessage>
            
            {loading ? (
              <div style={{ textAlign: 'center', margin: '2rem 0' }}>
                <p>Generating your account credentials...</p>
                {/* You could add a spinner here */}
              </div>
            ) : error ? (
              <ErrorMessage>{error}</ErrorMessage>
            ) : credentialsViewed && !credentialsRevealed ? (
              <div>
                <div style={{ textAlign: 'center', margin: '2rem 0' }}>
                  <p>Your credentials have already been generated and displayed.</p>
                  <p>If you need to recover your credentials, please contact our support team.</p>
                  <div style={{ marginTop: '1.5rem' }}>
                    <a href="https://t.me/deserved0" target="_blank" rel="noopener noreferrer">
                      <Button secondary>Contact Support</Button>
                    </a>
                  </div>
                </div>
              </div>
            ) : !credentialsRevealed ? (
              <HiddenCredentialsWrapper>
                <h3>Your Account Credentials Are Ready</h3>
                <p>Your account has been created successfully. You can now reveal your login credentials.</p>
                <p><strong>Important:</strong> Once revealed, your credentials will only be visible until you leave or refresh this page.</p>
                <RevealButton primary onClick={markCredentialsAsViewed}>
                  Reveal My Credentials
                </RevealButton>
              </HiddenCredentialsWrapper>
            ) : (
              <div>
                <h3>Your Account Credentials</h3>
                <p>Please save these credentials safely. You'll need them to log in to the application:</p>
                
                <UserCredentials>
                  <CredentialItem>
                    <strong>User ID:</strong> {userCredentials?.user_id}
                  </CredentialItem>
                  <CredentialItem>
                    <strong>Access Code:</strong> {userCredentials?.access_code}
                  </CredentialItem>
                  {paymentInfo?.plan_id && (
                    <CredentialItem>
                      <strong>Plan:</strong> {paymentInfo.plan_id === 'lifetime' 
                        ? 'Lifetime License' 
                        : paymentInfo.plan_id.charAt(0).toUpperCase() + paymentInfo.plan_id.slice(1)}
                      {paymentInfo.plan_id === 'lifetime' && paymentInfo.amount === 250 && (
                        <span style={{ color: '#ff5252', marginLeft: '5px', fontWeight: 'bold' }}>
                          (Early Access)
                        </span>
                      )}
                    </CredentialItem>
                  )}
                  {paymentInfo?.expires_at && (
                    <CredentialItem>
                      <strong>Valid Until:</strong> {paymentInfo.plan_id === 'lifetime' 
                        ? 'Never Expires (Lifetime)' 
                        : new Date(paymentInfo.expires_at).toLocaleDateString()}
                    </CredentialItem>
                  )}
                </UserCredentials>
                
                {showCountdown && <Countdown seconds={300} />}
                
                <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#ef4444', marginBottom: '1.5rem' }}>
                  IMPORTANT: Copy these details now! They will be hidden if you refresh or leave this page.
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <Button 
                    primary
                    onClick={downloadCredentials}
                    style={{ maxWidth: '300px', margin: '0 auto' }}
                  >
                    Download Credentials
                  </Button>
                </div>
                
                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <p>If you lose these credentials, please contact our support team to recover them.</p>
                </div>
                
                {paymentInfo && (
                  <div style={{ marginTop: '2rem' }}>
                    <h4>Payment Details</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}><strong>Payment ID:</strong></td>
                          <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{paymentInfo.payment_id}</td>
                        </tr>
                        {paymentInfo.order_id && (
                          <tr>
                            <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}><strong>Order ID:</strong></td>
                            <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{paymentInfo.order_id}</td>
                          </tr>
                        )}
                        {paymentInfo.plan_id && (
                          <tr>
                            <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}><strong>Plan:</strong></td>
                            <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{paymentInfo.plan_id}</td>
                          </tr>
                        )}
                        {paymentInfo.pay_address && (
                          <tr>
                            <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}><strong>Payment Address:</strong></td>
                            <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{paymentInfo.pay_address}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            
            <DownloadSection>
              <DownloadTitle>Download & Setup Instructions</DownloadTitle>
              
              {paymentInfo?.camera_product || paymentInfo?.plan_id === 'camera_lifetime' ? (
                <div>
                  <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                    <h4 style={{ color: '#6ea8ff', marginBottom: '1rem' }}>Virtual Camera Tool Download</h4>
                    <p style={{ color: '#b6d0ff', marginBottom: '2rem', fontSize: '1.1rem' }}>
                      Please contact Deserved on Telegram for your download link.
                    </p>
                    <a href="https://t.me/deserved0" target="_blank" rel="noopener noreferrer">
                      <Button primary style={{ fontSize: '1.1rem', padding: '0.75rem 2rem' }}>
                        Contact on Telegram
                      </Button>
                    </a>
                  </div>
                </div>
              ) : (
                <div>
                  <DownloadInstructions>
                    <ol>
                      <li>Download the AccountWarmer application below</li>
                      <li>Install the application on your computer</li>
                      <li>Launch the application and enter your User ID and Access Code when prompted</li>
                      <li>Follow the in-app instructions to start warming your profiles</li>
                    </ol>
                  </DownloadInstructions>
                  
                  <ButtonContainer>
                    <div className="flex flex-col gap-2">
                      <h4 className="font-semibold mb-2">Windows:</h4>
                      <a href={getDownloadUrl('Account-Warmer-Setup-VERSION.exe')} target="_blank" rel="noopener noreferrer">
                        <Button primary>Download for Windows (x64)</Button>
                      </a>
                    </div>

                    <div className="flex flex-col gap-2">
                      <h4 className="font-semibold mb-2">macOS:</h4>
                      <a href={getDownloadUrl('Account-Warmer-VERSION-arm64.dmg')} target="_blank" rel="noopener noreferrer">
                        <Button primary>Download for Mac (Apple Silicon)</Button>
                      </a>
                      <a href={getDownloadUrl('Account-Warmer-VERSION-arm64-mac.zip')} target="_blank" rel="noopener noreferrer">
                        <Button secondary>Download for Mac (ZIP)</Button>
                      </a>
                    </div>

                    <div className="flex flex-col gap-2">
                      <h4 className="font-semibold mb-2">Linux:</h4>
                      <a href={getDownloadUrl('Account-Warmer-VERSION-arm64.AppImage')} target="_blank" rel="noopener noreferrer">
                        <Button primary>Download for Linux (ARM64)</Button>
                      </a>
                    </div>
                  </ButtonContainer>

                  <div className="mt-4 text-sm text-gray-600">
                    <p>Note: All downloads are for version {latestVersion || 'latest'}. If you need a different architecture, please visit our <a href="https://github.com/deservedd/deservedwarmer/releases/latest" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">releases page</a>.</p>
                  </div>
                </div>
              )}
            </DownloadSection>
            
            <div>
              <h3>Need Help?</h3>
              <p>If you encounter any issues, please contact our support team.</p>
              
              <ButtonContainer>
                <a href="https://t.me/deserved0" target="_blank" rel="noopener noreferrer">
                  <Button secondary>Contact Support</Button>
                </a>
                <Link to="/">
                  <Button secondary>Return to Home</Button>
                </Link>
              </ButtonContainer>
            </div>
          </SuccessContainer>
        </Container>
      </PageWrapper>
    </Layout>
  );
}

export default SuccessPage; 